import { oids } from "../../../configs/Settings";

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

type Identifier = { system?: string; value?: string };

function upsertCnpjIdentifier(params: {
  identifiers: Identifier[];
  cnpjDigits: string;
}): Identifier[] {
  const { identifiers, cnpjDigits } = params;
  const cpfSystem = `urn:oid:${oids.cpf}`;
  const cnpjSystem = `urn:oid:${oids.cnpj}`;

  const existing = identifiers ?? [];
  const idxCpf = existing.findIndex((id) => id?.system === cpfSystem);
  const idxCnpj = existing.findIndex((id) => id?.system === cnpjSystem);

  // Regra: se já existe CNPJ, atualizamos o value; senão, migramos o identifier CPF; senão, adicionamos.
  if (idxCnpj >= 0) {
    const next = [...existing];
    next[idxCnpj] = { ...next[idxCnpj], system: cnpjSystem, value: cnpjDigits };
    return next;
  }

  if (idxCpf >= 0) {
    const next = [...existing];
    next[idxCpf] = { ...next[idxCpf], system: cnpjSystem, value: cnpjDigits };
    return next;
  }

  if (existing.length > 0) {
    // Fallback seguro: preserva os demais identifiers e garante que CNPJ exista.
    return [...existing, { system: cnpjSystem, value: cnpjDigits }];
  }

  return [{ system: cnpjSystem, value: cnpjDigits }];
}

function ensureProvType(
  type: fhir5.Organization["type"],
): fhir5.Organization["type"] {
  const nextType = Array.isArray(type) ? [...type] : [];
  const first = nextType[0] ?? {};
  const firstCoding = Array.isArray(first.coding) ? [...first.coding] : [];
  const coding0 = firstCoding[0] ?? {};

  firstCoding[0] = {
    ...coding0,
    system: "http://terminology.hl7.org/CodeSystem/organization-type",
    code: "prov",
    display: "Healthcare Provider",
  };

  nextType[0] = { ...first, coding: firstCoding };
  return nextType;
}

/**
 * Normaliza o payload da migração CPF→CNPJ para ser usado em PUT.
 *
 * Intenção:
 * - Preservar 100% dos dados atuais (spread) e alterar SOMENTE o necessário.
 * - Garantir que os campos críticos (`type` e `identifier`) fiquem consistentes.
 */
export function normalizeMigrationCpfToCnpj(params: {
  currentOrganization: fhir5.Organization;
  newCnpjDigits: string;
  validatedOrganization?: fhir5.Organization | null;
}): fhir5.Organization {
  const { currentOrganization, newCnpjDigits, validatedOrganization } = params;
  const cnpjDigits = onlyDigits(newCnpjDigits);
  if (!cnpjDigits) {
    throw new Error("normalizeMigrationCpfToCnpj: CNPJ inválido");
  }

  if (!validatedOrganization) {
    throw new Error(
      "normalizeMigrationCpfToCnpj: validatedOrganization é obrigatório (retorno do Validate)",
    );
  }

  // Decisão intencional (simplificação):
  // - Preferimos usar diretamente o retorno tratado do `Validate` (FHIR ou alt-normalizado),
  //   para evitar “merge” com dados antigos e reduzir risco de inconsistência.
  // - Mantemos SOMENTE as `extension` da organização atual (logo, manager, etc.)
  //   e garantimos que o PUT atualize o mesmo recurso (id atual).
  const base = validatedOrganization as fhir5.Organization;

  const next: fhir5.Organization = {
    ...(base as fhir5.Organization),
    resourceType: "Organization",
    id: currentOrganization?.id || base?.id,
    extension: currentOrganization?.extension ?? base?.extension,
    identifier: upsertCnpjIdentifier({
      identifiers: (base?.identifier ?? []) as Identifier[],
      cnpjDigits,
    }) as unknown as fhir5.Organization["identifier"],
    type: ensureProvType(base?.type),
  };

  return next;
}
